import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Video, Loader2, Link2, ExternalLink, Mail, Sparkles, X, PhoneOff, Copy, Check, Mic, Square, FileDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectMeetings({ projectId }) {
    const { session, user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [data, setData] = useState({ active: [], history: [], jitsi_domain: 'meet.jit.si' });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [createForm, setCreateForm] = useState({ title: '', agenda: '' });
    const [createOpen, setCreateOpen] = useState(false);
    const [embedMeeting, setEmbedMeeting] = useState(null); // {meeting, jitsi_url}
    const [inviteOpen, setInviteOpen] = useState(null); // meeting object
    const [inviteForm, setInviteForm] = useState({ guest_name: '', guest_email: '', expires_hours: 24 });
    const [inviteResult, setInviteResult] = useState(null);
    const [summarizeOpen, setSummarizeOpen] = useState(null); // meeting
    const [transcript, setTranscript] = useState('');
    const [summaryResult, setSummaryResult] = useState(null);
    const [copied, setCopied] = useState(false);
    // MediaRecorder state for live in-browser audio capture
    const [recording, setRecording] = useState(false);
    const [recElapsed, setRecElapsed] = useState(0);
    const [autoRecord, setAutoRecord] = useState(() => localStorage.getItem('av.autoRecord') === '1');
    const recRef = React.useRef({ recorder: null, chunks: [], stream: null, timerId: null, autoChainMeeting: null });

    const toggleAutoRecord = () => {
        const next = !autoRecord;
        setAutoRecord(next);
        localStorage.setItem('av.autoRecord', next ? '1' : '0');
        toast.success(next
            ? 'Auto-transcription activée — vous serez prévenu·e à chaque réunion'
            : 'Auto-transcription désactivée');
    };

    const auth = useMemo(() => session?.access_token ? { headers: { Authorization: `Bearer ${session.access_token}` } } : null, [session]);

    const reload = async () => {
        if (!auth || !currentWorkspace) { setLoading(false); return; }
        setLoading(true);
        try {
            const r = await axios.get(`${API}/projects/${projectId}/meetings`,
                { ...auth, params: { workspace_id: currentWorkspace.id } });
            setData(r.data);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId, currentWorkspace]);

    const startMeeting = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/projects/${projectId}/meetings/create`,
                { project_id: projectId, workspace_id: currentWorkspace.id, ...createForm }, auth);
            setCreateOpen(false);
            setCreateForm({ title: '', agenda: '' });
            setEmbedMeeting({ meeting: r.data.meeting, jitsi_url: r.data.jitsi_url });
            toast.success('Réunion démarrée');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const joinMeeting = async (m) => {
        try {
            const r = await axios.post(`${API}/meetings/${m.id}/join`, {}, auth);
            setEmbedMeeting({ meeting: m, jitsi_url: r.data.jitsi_url });
            // Auto-record kick-off (Q1-a: local mic only, Q2-b: per-user toggle)
            const autoEnabled = localStorage.getItem('av.autoRecord') === '1';
            if (autoEnabled && navigator.mediaDevices?.getUserMedia) {
                // Explicit consent prompt — RGPD obligation, even though pref is opt-in
                const consent = window.confirm(
                    "Auto-transcription activée — votre microphone sera enregistré pendant " +
                    "cette réunion. À la fin, l'audio sera transcrit, synthétisé puis enregistré " +
                    "automatiquement dans la GED du projet. L'audio brut est ensuite supprimé.\n\n" +
                    "Démarrer l'enregistrement maintenant ?"
                );
                if (consent) {
                    try {
                        await startRecording(/* silent = */ true);
                        toast.success('Auto-enregistrement démarré', { icon: '🔴' });
                    } catch { /* startRecording already toasts on failure */ }
                }
            }
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const endMeeting = async (m, notes) => {
        try {
            await axios.post(`${API}/meetings/${m.id}/end`, { notes: notes || null }, auth);
            // If auto-record is running, stop it and chain transcribe → summarize → save GED
            const wasRecording = recRef.current?.recorder && recRef.current.recorder.state !== 'inactive';
            if (wasRecording) {
                stopRecording(/* autoChain = */ m);
                toast('Traitement IA en cours…', { icon: '⏳' });
            } else {
                setEmbedMeeting(null);
                reload();
            }
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    // Run the full pipeline: transcribe audio → summarize → upload .docx to GED
    const runAutoPipeline = async (file, meeting) => {
        setBusy(true);
        try {
            // 1) Transcribe via Whisper
            const fd = new FormData();
            fd.append('file', file);
            fd.append('language', 'fr');
            const tx = await axios.post(`${API}/transcribe`, fd, {
                headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
            });
            const text = (tx.data?.text || '').trim();
            if (text.length < 20) {
                toast.error('Transcription trop courte — synthèse annulée');
                return;
            }
            // 2) Summarize via Claude
            const sum = await axios.post(`${API}/meetings/${meeting.id}/summarize`,
                { transcript: text, language: 'fr' }, auth);
            // 3) Save docx to GED
            const ged = await axios.post(`${API}/meetings/${meeting.id}/save-to-ged`,
                { transcript: text, summary_json: sum.data?.summary }, auth);
            toast.success(`Compte-rendu archivé en GED : ${ged.data?.document?.name}`, { duration: 5000 });
        } catch (e) {
            toast.error('Pipeline auto échoué : ' + (e.response?.data?.detail || e.message));
        } finally {
            setBusy(false);
            setEmbedMeeting(null);
            reload();
        }
    };

    const sendInvite = async (e) => {
        e.preventDefault();
        if (!inviteOpen) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API}/meetings/${inviteOpen.id}/invite-external`, inviteForm, auth);
            setInviteResult(r.data);
            toast.success(inviteForm.guest_email ? 'Invitation envoyée par email' : 'Lien d\'invitation généré');
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const copyInviteUrl = async () => {
        if (!inviteResult?.public_url) return;
        await navigator.clipboard.writeText(inviteResult.public_url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const summarize = async () => {
        if (transcript.trim().length < 20) { toast.error('Transcription trop courte (min 20 caractères)'); return; }
        setBusy(true);
        try {
            const r = await axios.post(`${API}/meetings/${summarizeOpen.id}/summarize`, { transcript, language: 'fr' }, auth);
            setSummaryResult(r.data.summary);
            toast.success('Synthèse IA générée');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const saveSummaryToGED = async () => {
        if (!summarizeOpen || !summaryResult) return;
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/meetings/${summarizeOpen.id}/save-to-ged`,
                { transcript, summary_json: summaryResult },
                auth,
            );
            toast.success(`Compte-rendu archivé en GED : ${r.data?.document?.name}`, { duration: 5000 });
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
        setBusy(false);
    };

    const transcribeAudio = async (file) => {
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) {
            toast.error('Fichier > 25 MB. Découpez l\'enregistrement.');
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('language', 'fr');
            const r = await axios.post(`${API}/transcribe`, fd, {
                headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
            });
            const text = r.data?.text || '';
            setTranscript(prev => (prev ? prev + '\n\n' : '') + text);
            toast.success(`Transcription terminée — ${text.length} caractères`);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const startRecording = async (silent = false) => {
        if (!navigator.mediaDevices?.getUserMedia) {
            toast.error('Votre navigateur ne supporte pas l\'enregistrement audio.');
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Pick a widely supported MIME type
            const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
                .find(t => MediaRecorder.isTypeSupported(t)) || '';
            const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            const chunks = [];
            recorder.ondataavailable = (e) => { if (e.data?.size > 0) chunks.push(e.data); };
            recorder.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
                const ext = (recorder.mimeType || 'audio/webm').includes('mp4') ? 'mp4' : 'webm';
                const file = new File([blob], `meeting-${Date.now()}.${ext}`, { type: blob.type });
                const chainTo = recRef.current?.autoChainMeeting;
                if (chainTo) {
                    await runAutoPipeline(file, chainTo);
                } else {
                    await transcribeAudio(file);
                }
            };
            recRef.current = { recorder, chunks, stream, timerId: null, autoChainMeeting: null };
            recorder.start(1000); // collect 1s chunks
            setRecording(true);
            setRecElapsed(0);
            recRef.current.timerId = setInterval(() => setRecElapsed(t => t + 1), 1000);
        } catch (e) {
            if (!silent) toast.error('Accès au microphone refusé : ' + (e.message || ''));
            throw e;
        }
    };

    const stopRecording = (autoChainMeeting = null) => {
        const cur = recRef.current || {};
        if (cur.timerId) clearInterval(cur.timerId);
        if (autoChainMeeting) cur.autoChainMeeting = autoChainMeeting;
        if (cur.recorder && cur.recorder.state !== 'inactive') cur.recorder.stop();
        setRecording(false);
    };

    React.useEffect(() => () => {
        // Cleanup on unmount: stop any active recording so we don't leak the mic
        const { recorder, stream, timerId } = recRef.current || {};
        if (timerId) clearInterval(timerId);
        if (recorder && recorder.state !== 'inactive') recorder.stop();
        if (stream) stream.getTracks().forEach(t => t.stop());
    }, []);

    if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-6" data-testid="project-meetings">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-1">Visioconférence · Jitsi Meet</div>
                    <h2 className="font-heading text-2xl">Réunions de pilotage</h2>
                    <p className="text-sm text-foreground/70 mt-2 max-w-2xl">
                        Échangez en visio avec les membres du projet et invitez ponctuellement des bailleurs via un lien sécurisé.
                        Toutes les réunions sont journalisées dans l'audit log.
                    </p>
                </div>
                <Button onClick={() => setCreateOpen(true)} className="bg-primary hover:bg-primary-hover" data-testid="meeting-start-btn">
                    <Video size={14} className="mr-2" /> Démarrer une réunion
                </Button>
            </div>

            {/* Active meetings */}
            {data.active.length > 0 && (
                <div className="card-soft p-5 border-primary/30 bg-primary/5">
                    <div className="eyebrow text-primary mb-3">🔴 En cours</div>
                    <div className="space-y-3">
                        {data.active.map(m => (
                            <div key={m.id} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`meeting-active-${m.id}`}>
                                <div className="min-w-0">
                                    <div className="font-medium truncate">{m.title}</div>
                                    <div className="text-xs text-muted-foreground">
                                        Démarrée {new Date(m.started_at).toLocaleString('fr-FR')} · {m.participants?.length || 0} participant(s)
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => setInviteOpen(m)} data-testid={`meeting-invite-${m.id}`}>
                                        <Mail size={14} className="mr-1.5" /> Inviter un externe
                                    </Button>
                                    <Button size="sm" onClick={() => joinMeeting(m)} className="bg-primary hover:bg-primary-hover" data-testid={`meeting-join-${m.id}`}>
                                        <Video size={14} className="mr-1.5" /> Rejoindre
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* History */}
            <div className="card-soft p-5">
                <div className="eyebrow mb-3">Historique</div>
                {data.history.length === 0 ? (
                    <p className="text-sm text-foreground/60">Aucune réunion passée.</p>
                ) : (
                    <ul className="divide-y divide-border" data-testid="meeting-history">
                        {data.history.slice(0, 20).map(m => {
                            const dur = m.ended_at && m.started_at
                                ? Math.round((new Date(m.ended_at) - new Date(m.started_at)) / 60000) : null;
                            return (
                                <li key={m.id} className="py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`meeting-history-${m.id}`}>
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{m.title}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {new Date(m.started_at || m.created_at).toLocaleString('fr-FR')}
                                            {dur !== null && <> · {dur} min</>}
                                            {' · '}{m.participants?.length || 0} pers.
                                            {m.summary_ai && <span className="text-emerald-600"> · synthèse IA ✓</span>}
                                        </div>
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={() => { setSummarizeOpen(m); setSummaryResult(m.summary_ai ? safeJsonParse(m.summary_ai) : null); setTranscript(m.notes || ''); }}
                                        data-testid={`meeting-summarize-${m.id}`}>
                                        <Sparkles size={14} className="mr-1.5" /> {m.summary_ai ? 'Voir synthèse' : 'Générer synthèse'}
                                    </Button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Nouvelle réunion</DialogTitle>
                        <DialogDescription>La réunion démarre immédiatement et est journalisée dans l'audit log.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input placeholder="Titre (ex: Point d'avancement S46)" value={createForm.title}
                            onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} data-testid="meeting-title" />
                        <Textarea placeholder="Ordre du jour (optionnel)" rows={3} value={createForm.agenda}
                            onChange={(e) => setCreateForm({ ...createForm, agenda: e.target.value })} data-testid="meeting-agenda" />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setCreateOpen(false)}>Annuler</Button>
                        <Button onClick={startMeeting} disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="meeting-create-submit">
                            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Video size={14} className="mr-2" />}
                            Démarrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Jitsi embed (full-screen) */}
            {embedMeeting && (
                <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="meeting-embed">
                    <div className="bg-foreground text-background px-4 py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{embedMeeting.meeting.title}</div>
                            <div className="text-xs opacity-60 truncate">Salle : {embedMeeting.meeting.room_name}</div>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => window.open(embedMeeting.jitsi_url, '_blank')} className="text-foreground"
                                data-testid="meeting-open-new-tab">
                                <ExternalLink size={14} className="mr-1.5" /> Nouvel onglet
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => endMeeting(embedMeeting.meeting)} data-testid="meeting-end-btn">
                                <PhoneOff size={14} className="mr-1.5" /> Terminer
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEmbedMeeting(null)} className="text-background" data-testid="meeting-close-embed">
                                <X size={14} />
                            </Button>
                        </div>
                    </div>
                    <iframe
                        src={`${embedMeeting.jitsi_url}#userInfo.displayName="${encodeURIComponent(user?.user_metadata?.full_name || user?.email || 'Membre')}"&config.startWithAudioMuted=false&config.startWithVideoMuted=false&config.prejoinPageEnabled=false`}
                        allow="camera; microphone; fullscreen; display-capture; autoplay"
                        className="flex-1 w-full"
                        title="Jitsi Meeting"
                        data-testid="meeting-iframe"
                    />
                </div>
            )}

            {/* Invite external dialog */}
            <Dialog open={!!inviteOpen} onOpenChange={(v) => { if (!v) { setInviteOpen(null); setInviteResult(null); setInviteForm({ guest_name: '', guest_email: '', expires_hours: 24 }); } }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Inviter un participant externe</DialogTitle>
                        <DialogDescription>Génère un lien sécurisé temporaire. Idéal pour les bailleurs ou consultants.</DialogDescription>
                    </DialogHeader>
                    {!inviteResult ? (
                        <form onSubmit={sendInvite} className="space-y-3">
                            <Input placeholder="Nom de l'invité (optionnel)" value={inviteForm.guest_name}
                                onChange={(e) => setInviteForm({ ...inviteForm, guest_name: e.target.value })} data-testid="invite-guest-name" />
                            <Input type="email" placeholder="Email de l'invité (pour recevoir le lien par email)" value={inviteForm.guest_email}
                                onChange={(e) => setInviteForm({ ...inviteForm, guest_email: e.target.value })} data-testid="invite-guest-email" />
                            <div>
                                <label className="text-sm font-medium block mb-1.5">Validité du lien</label>
                                <select value={inviteForm.expires_hours}
                                    onChange={(e) => setInviteForm({ ...inviteForm, expires_hours: Number(e.target.value) })}
                                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="invite-expires">
                                    <option value="1">1 heure</option>
                                    <option value="6">6 heures</option>
                                    <option value="24">24 heures</option>
                                    <option value="72">3 jours</option>
                                    <option value="168">7 jours</option>
                                </select>
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="invite-submit">
                                    {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Link2 size={14} className="mr-2" />}
                                    Générer le lien
                                </Button>
                            </DialogFooter>
                        </form>
                    ) : (
                        <div className="space-y-3" data-testid="invite-result">
                            <div className="bg-surface-muted p-3 rounded-md flex items-center justify-between gap-2">
                                <code className="text-xs break-all flex-1">{inviteResult.public_url}</code>
                                <Button size="sm" variant="outline" onClick={copyInviteUrl} data-testid="invite-copy">
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {inviteForm.guest_email
                                    ? `Lien envoyé à ${inviteForm.guest_email}. `
                                    : ''}
                                Expire {new Date(inviteResult.invite?.expires_at).toLocaleString('fr-FR')}.
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Summarize dialog */}
            <Dialog open={!!summarizeOpen} onOpenChange={(v) => { if (!v) { setSummarizeOpen(null); setSummaryResult(null); setTranscript(''); } }}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Synthèse IA · {summarizeOpen?.title}</DialogTitle>
                        <DialogDescription>Collez la transcription ou les notes brutes. Claude génère résumé, décisions, actions et risques.</DialogDescription>
                    </DialogHeader>
                    {!summaryResult ? (
                        <div className="space-y-3">
                            <div className="border border-dashed border-border rounded-lg p-4 bg-surface-muted/40">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-start gap-2.5">
                                        <Mic size={16} className="text-secondary mt-0.5 flex-shrink-0" />
                                        <div className="text-xs text-foreground/75">
                                            <div className="font-medium text-foreground mb-0.5">Transcription automatique (Whisper)</div>
                                            Enregistrez directement depuis votre microphone, ou importez un fichier (≤ 25 MB).
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {!recording ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={startRecording}
                                                disabled={busy}
                                                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                                data-testid="audio-record-btn"
                                            >
                                                <Mic size={14} className="mr-2" /> Enregistrer
                                            </Button>
                                        ) : (
                                            <Button
                                                size="sm"
                                                onClick={stopRecording}
                                                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground animate-pulse"
                                                data-testid="audio-stop-btn"
                                            >
                                                <Square size={12} className="mr-2 fill-current" />
                                                Arrêter · {Math.floor(recElapsed / 60).toString().padStart(2, '0')}:{(recElapsed % 60).toString().padStart(2, '0')}
                                            </Button>
                                        )}
                                        <label className="inline-flex">
                                            <input
                                                type="file"
                                                accept="audio/*,.mp3,.m4a,.wav,.webm,.mp4,.mpeg,.mpga"
                                                className="hidden"
                                                onChange={(e) => { const f = e.target.files?.[0]; if (f) transcribeAudio(f); e.target.value = ''; }}
                                                data-testid="audio-upload-input"
                                            />
                                            <Button asChild size="sm" variant="outline" disabled={busy || recording} data-testid="audio-upload-btn">
                                                <span className="cursor-pointer">
                                                    {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Mic size={14} className="mr-2" />}
                                                    Importer un fichier
                                                </span>
                                            </Button>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <Textarea rows={10} placeholder="Collez ici la transcription ou les notes brutes de la réunion…"
                                value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="summary-transcript" />
                            <DialogFooter>
                                <Button onClick={summarize} disabled={busy || transcript.length < 20} className="bg-primary hover:bg-primary-hover" data-testid="summary-submit">
                                    {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-2" />}
                                    Générer la synthèse
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <div className="space-y-4 text-sm" data-testid="summary-result">
                            <Section title="Résumé">
                                <p>{summaryResult.resume}</p>
                            </Section>
                            <Section title="Décisions">
                                {summaryResult.decisions?.length ? (
                                    <ul className="list-disc pl-5 space-y-1">{summaryResult.decisions.map((d, i) => <li key={i}>{d}</li>)}</ul>
                                ) : <p className="text-foreground/50">—</p>}
                            </Section>
                            <Section title="Actions">
                                {summaryResult.actions?.length ? (
                                    <ul className="space-y-2">{summaryResult.actions.map((a, i) => (
                                        <li key={i} className="border border-border rounded-md p-2">
                                            <div className="font-medium">{a.task}</div>
                                            <div className="text-xs text-muted-foreground">{a.assignee || '—'} · {a.due || 'sans échéance'}</div>
                                        </li>
                                    ))}</ul>
                                ) : <p className="text-foreground/50">—</p>}
                            </Section>
                            <Section title="Risques identifiés">
                                {summaryResult.risques?.length ? (
                                    <ul className="list-disc pl-5 space-y-1 text-amber-700">{summaryResult.risques.map((r, i) => <li key={i}>{r}</li>)}</ul>
                                ) : <p className="text-foreground/50">—</p>}
                            </Section>
                            <DialogFooter className="gap-2 sm:gap-2 mt-4 pt-4 border-t border-border">
                                <Button
                                    onClick={saveSummaryToGED}
                                    disabled={busy}
                                    className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                                    data-testid="summary-save-ged"
                                >
                                    {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <FileDown size={14} className="mr-2" />}
                                    Sauvegarder en GED (.docx)
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

const Section = ({ title, children }) => (
    <div>
        <div className="eyebrow mb-2">{title}</div>
        {children}
    </div>
);

const safeJsonParse = (s) => {
    try { return typeof s === 'string' ? JSON.parse(s) : s; } catch { return null; }
};
